import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    Switch,
    message,
    Descriptions,
    Radio,
    Alert,
    Tooltip,
    Divider,
    Empty,
    InputNumber
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    SafetyCertificateOutlined
} from '@ant-design/icons';
import hostService from '../../../shared/services/hostService';
import { userPath } from '../../../shared/config/paths';
import { buildUserSiteSslPayload } from './siteFormHelpers';

const { Option } = Select;
const { TextArea } = Input;

const MySites = ({ containerId: propContainerId, embedded = false }) => {
    const { t } = useTranslation();
    const [sites, setSites] = useState([]);
    const [containers, setContainers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [currentDetail, setCurrentDetail] = useState(null);
    const [form] = Form.useForm();
    const [systemCertForm] = Form.useForm();
    const [sslCertificates, setSslCertificates] = useState({ host: [], system: [] });
    const [sslLoading, setSslLoading] = useState(false);
    const [systemCertModalVisible, setSystemCertModalVisible] = useState(false);
    const [systemCertLoading, setSystemCertLoading] = useState(false);
    const [targetSite, setTargetSite] = useState(null);
    const selectedContainerId = Form.useWatch('container_id', form);

    const selectedContainer = useMemo(
        () => containers.find((container) => container.id === selectedContainerId),
        [containers, selectedContainerId]
    );
    const selectedNodeId = selectedContainer?.node_id || selectedContainer?.node?.id || null;

    const availableHostCertificates = useMemo(() => {
        if (!selectedNodeId) {
            return sslCertificates.host;
        }
        return sslCertificates.host.filter((cert) => !cert.node_id || cert.node_id === selectedNodeId);
    }, [selectedNodeId, sslCertificates.host]);
    const hasSystemCertificates = sslCertificates.system.length > 0;

    const loadSslCertificates = useCallback(async () => {
        setSslLoading(true);
        try {
            const response = await hostService.getSslCertificates();
            if (response.success) {
                const data = response.data || {};
                setSslCertificates({
                    host: data.host_certificates || [],
                    system: data.system_certificates || [],
                });
            } else {
                message.error(response.error || t('Failed to load SSL certificates'));
            }
        } catch (error) {
            message.error(t('Failed to load SSL certificates'));
        } finally {
            setSslLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSites();
        loadContainers();
        loadSslCertificates();

        // 嵌入模式时预填充containerId
        if (embedded && propContainerId) {
            form.setFieldsValue({ container_id: propContainerId });
        } else {
            // 从URL参数获取container_id并预填充
            const urlParams = new URLSearchParams(window.location.search);
            const containerIdParam = urlParams.get('container_id');
            if (containerIdParam) {
                form.setFieldsValue({ container_id: parseInt(containerIdParam) });
            }
        }
    }, [form, loadSslCertificates, propContainerId, embedded]);

    const loadSites = async () => {
        setLoading(true);
        try {
            // 嵌入模式时只加载指定容器的站点
            const response = embedded && propContainerId
                ? await hostService.getSites(propContainerId)
                : await hostService.getSites();
            if (response.success) {
                setSites(response.data || []);
            } else {
                message.error(response.error || t('Failed to retrieve sites'));
            }
        } catch (error) {
            message.error(t('Failed to retrieve sites'));
        } finally {
            setLoading(false);
        }
    };

    const loadContainers = async () => {
        try {
            const response = await hostService.getContainers();
            if (response.success) {
                setContainers(response.data || []);
            }
        } catch (error) {
            console.error('获取容器列表失败:', error);
        }
    };

    const handleCreate = () => {
        setEditingRecord(null);
        form.resetFields();
        form.setFieldsValue({
            ssl_enabled: false,
            ssl_mode: 'existing',
            force_https: true,
            hsts: false,
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        const sslConfig = record.ssl_config || null;
        let sslMode = 'existing';
        let hostSslCertId;
        let customSslConfig = '';

        if (sslConfig) {
            const matchedCert = sslCertificates.host.find(
                (cert) => cert.cert_path === sslConfig.certificate && cert.key_path === sslConfig.key
            );
            if (matchedCert) {
                hostSslCertId = matchedCert.id;
            } else {
                sslMode = 'custom';
                customSslConfig = JSON.stringify(sslConfig, null, 2);
            }
        }

        form.setFieldsValue({
            ...record,
            host_ssl_cert_id: hostSslCertId,
            custom_ssl_config: customSslConfig,
            ssl_mode: sslMode,
            ssl_enabled: !!record.ssl_enabled,
            force_https: sslConfig?.force_https ?? true,
            hsts: sslConfig?.hsts ?? false,
        });
        setModalVisible(true);
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const { ssl, error } = buildUserSiteSslPayload(values, sslCertificates.host);
            if (error) {
                message.error(error);
                return;
            }

            const payload = {
                ...values,
                ssl: ssl || undefined,
            };

            delete payload.ssl_mode;
            delete payload.host_ssl_cert_id;
            delete payload.custom_ssl_config;
            delete payload.force_https;
            delete payload.hsts;
            delete payload.ssl_config;
            if (!payload.ssl) {
                delete payload.ssl;
            }

            let response;
            if (editingRecord) {
                response = await hostService.updateSite({ ...payload, id: editingRecord.id });
            } else {
                response = await hostService.createSite(payload);
            }

            if (response.success) {
                message.success(editingRecord ? t('Site updated successfully') : t('Site created successfully'));
                setModalVisible(false);
                form.resetFields();
                loadSites();
            } else {
                message.error(response.error || t('Operation failed'));
            }
        } catch (error) {
            message.error(t('Operation failed'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        Modal.confirm({
            title: t('Delete the site?'),
            content: t('Deleting this site permanently deletes its configuration. This cannot be undone!'),
            okType: 'danger',
            onOk: async () => {
                try {
                    const response = await hostService.deleteSite(id);
                    if (response.success) {
                        message.success(t('Site deleted successfully'));
                        loadSites();
                    } else {
                        message.error(response.error || t('Failed to delete site'));
                    }
                } catch (error) {
                    message.error(t('Failed to delete site'));
                }
            }
        });
    };

    const openSystemCertModal = async (record) => {
        setTargetSite(record);
        await loadSslCertificates();
        systemCertForm.setFieldsValue({
            ssl_certificate_id: undefined,
            force_https: record?.ssl_config?.force_https ?? true,
            hsts: record?.ssl_config?.hsts ?? false,
        });
        setSystemCertModalVisible(true);
    };

    const handleApplySystemCert = async () => {
        if (!targetSite) return;
        try {
            const values = await systemCertForm.validateFields();
            setSystemCertLoading(true);
            const payload = {
                ssl_certificate_id: values.ssl_certificate_id,
                site_id: targetSite.id,
                domain: targetSite.domain,
                force_https: values.force_https,
                hsts: values.hsts,
            };
            const response = await hostService.useSystemCert(payload);
            if (response.success) {
                message.success(t('System certificate bound successfully'));
                setSystemCertModalVisible(false);
                systemCertForm.resetFields();
                setTargetSite(null);
                loadSites();
                loadSslCertificates();
            } else {
                message.error(response.error || t('Binding failed'));
            }
        } catch (error) {
            if (error.errorFields) {
                message.error(t('Please check the form'));
            } else {
                message.error(t('Binding failed: {{message}}', { message: error.message }));
            }
        } finally {
            setSystemCertLoading(false);
        }
    };

    const handleToggleStatus = async (record) => {
        try {
            const response = record.is_enabled
                ? await hostService.disableSite(record.id)
                : await hostService.enableSite(record.id);

            if (response.success) {
                message.success(record.is_enabled ? t('Site disabled') : t('Site enabled'));
                loadSites();
            } else {
                message.error(response.error || t('Operation failed'));
            }
        } catch (error) {
            message.error(t('Operation failed'));
        }
    };

    const handleViewDetail = async (record) => {
        try {
            const response = await hostService.getSite(record.id);
            if (response.success) {
                setCurrentDetail(response.data);
                setDetailModalVisible(true);
            } else {
                message.error(response.error || t('Failed to retrieve site details'));
            }
        } catch (error) {
            message.error(t('Failed to retrieve site details'));
        }
    };

    const getProxyTypeText = (type) => {
        const typeMap = {
            php: 'PHP',
            static: t('Static files'),
            proxy: t('Reverse proxy'),
            redirect: t('Redirect'),
        };
        return typeMap[type] || type;
    };

    const columns = [
        {
            title: t('Domains'),
            dataIndex: 'domain',
            key: 'domain',
            render: (domain, record) => (
                <Space direction="vertical" size="small">
                    <span>{domain}</span>
                    {record.server_aliases && (
                        <span style={{ fontSize: '12px', color: '#999' }}>
                            {t('Aliases: {{aliases}}', { aliases: record.server_aliases })}
                        </span>
                    )}
                </Space>
            ),
        },
        {
            title: t('Container'),
            dataIndex: 'container_id',
            key: 'container_id',
            render: (containerId) => {
                const container = containers.find(c => c.id === containerId);
                return container ? container.name : t('Container #{{containerId}}', { containerId });
            }
        },
        {
            title: t('Proxy type'),
            dataIndex: 'proxy_type',
            key: 'proxy_type',
            render: (type) => getProxyTypeText(type),
        },
        {
            title: 'SSL',
            dataIndex: 'ssl_enabled',
            key: 'ssl_enabled',
            render: (enabled) => (
                <Tag color={enabled ? 'success' : 'default'}>
                    {enabled ? t('Enabled') : t('Disabled')}
                </Tag>
            ),
        },
        {
            title: t('Status'),
            dataIndex: 'is_enabled',
            key: 'is_enabled',
            render: (enabled, record) => (
                <Switch
                    checked={enabled}
                    checkedChildren={t('Enable')}
                    unCheckedChildren={t('Disable')}
                    onChange={() => handleToggleStatus(record)}
                />
            ),
        },
        {
            title: t('Created at'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-',
        },
        {
            title: t('Actions'),
            key: 'action',
            fixed: 'right',
            width: 260,
            render: (_, record) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetail(record)}
                    >
                        {t('Details')}
                    </Button>
                    <Tooltip title={hasSystemCertificates ? t('Bind a system certificate to the site') : t('No system certificates available')}>
                        <Button
                            size="small"
                            icon={<SafetyCertificateOutlined />}
                            disabled={!hasSystemCertificates}
                            onClick={() => openSystemCertModal(record)}
                        >
                            {t('System certificate')}
                        </Button>
                    </Tooltip>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        {t('Edit')}
                    </Button>
                    <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record.id)}
                    >
                        {t('Delete')}
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                {!embedded && <h2>{t('My sites')}</h2>}
                {embedded && <div />}
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                >
                    {t('Create site')}
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={sites}
                rowKey="id"
                loading={loading}
                scroll={{ x: 1200 }}
                locale={{
                    emptyText: loading ? ' ' : (
                        <Empty description={t('No sites yet. Click "Create site" in the top right to add one.')} />
                    ),
                }}
                pagination={{
                    showSizeChanger: true,
                    showTotal: (total) => t('{{total}} sites total', { total }),
                }}
            />

            {/* 创建/编辑站点模态框 */}
            <Modal
                title={editingRecord ? t('Edit site') : t('Create site')}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
                confirmLoading={submitting}
                okText={editingRecord ? t('Save') : t('Create')}
                width={700}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        name="container_id"
                        label={t('Select a container')}
                        rules={[{ required: true, message: t('Select a container') }]}
                    >
                        <Select placeholder={t('Select a container')}>
                            {containers.map(container => (
                                <Option key={container.id} value={container.id}>
                                    {container.name} ({container.container_type})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="domain"
                        label={t('Primary domain')}
                        rules={[
                            { required: true, message: t('Please enter the primary domain') },
                            { pattern: /^[a-zA-Z0-9][-a-zA-Z0-9]{0,62}(\.[a-zA-Z0-9][-a-zA-Z0-9]{0,62})+$/, message: t('Enter a valid domain') }
                        ]}
                    >
                        <Input placeholder={t('E.g. example.com')} />
                    </Form.Item>

                    <Form.Item
                        name="server_aliases"
                        label={t('Domain alias')}
                    >
                        <Input placeholder={t('Separate multiple domains with spaces, e.g., www.example.com api.example.com')} />
                    </Form.Item>

                    <Form.Item
                        name="proxy_type"
                        label={t('Proxy type')}
                        rules={[{ required: true, message: t('Select a proxy type') }]}
                    >
                        <Select placeholder={t('Select a proxy type')}>
                            <Option value="php">PHP</Option>
                            <Option value="static">{t('Static files')}</Option>
                            <Option value="proxy">{t('Reverse proxy')}</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.proxy_type !== cur.proxy_type}>
                        {({ getFieldValue }) =>
                            getFieldValue('proxy_type') === 'proxy' ? (
                                <Form.Item
                                    name="proxy_target"
                                    label={t('App port')}
                                    tooltip={t('Port your app listens on inside this container')}
                                    rules={[{ required: true, message: t('Please enter a port') }]}
                                    getValueProps={(v) => ({ value: typeof v === 'string' ? (v.match(/:(\d+)\/?$/)?.[1] ?? v) : v })}
                                >
                                    <InputNumber min={1} max={65535} placeholder="3000" style={{ width: '100%' }} />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>

                    <Form.Item
                        name="root_path"
                        label={t('Root directory')}
                        rules={[{ required: true, message: t('Enter the root directory') }]}
                    >
                        <Input placeholder={t('E.g. /var/www/html')} />
                    </Form.Item>

                    <Form.Item
                        name="index_file"
                        label={t('Index file')}
                    >
                        <Input placeholder={t('E.g. index.php index.html')} />
                    </Form.Item>

                    <Form.Item
                        name="ssl_enabled"
                        label={t('Enable SSL')}
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.ssl_enabled !== currentValues.ssl_enabled ||
                            prevValues.ssl_mode !== currentValues.ssl_mode
                        }
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('ssl_enabled') ? (
                                <>
                                    <Alert
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: 12 }}
                                        message={t('Notice')}
                                        description={t('Select an uploaded or issued certificate, or use a custom JSON configuration. For more certificates, go to “My certificates”.')}
                                    />
                                    <Form.Item
                                        name="ssl_mode"
                                        label={t('Configuration method')}
                                        rules={[{ required: true, message: t('Select a configuration method') }]}
                                    >
                                        <Radio.Group optionType="button">
                                            <Radio.Button value="existing">{t('Use existing certificate')}</Radio.Button>
                                            <Radio.Button value="custom">{t('Custom JSON')}</Radio.Button>
                                        </Radio.Group>
                                    </Form.Item>
                                    <Space size="middle" style={{ width: '100%', marginBottom: 12 }}>
                                        <Form.Item
                                            name="force_https"
                                            label={t('Force HTTPS')}
                                            valuePropName="checked"
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Switch />
                                        </Form.Item>
                                        <Form.Item
                                            name="hsts"
                                            label={t('Enable HSTS')}
                                            valuePropName="checked"
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Switch />
                                        </Form.Item>
                                        <Button size="small" onClick={loadSslCertificates} loading={sslLoading}>
                                            {t('Refresh certificate')}
                                        </Button>
                                    </Space>
                                    {getFieldValue('ssl_mode') === 'existing' ? (
                                        <>
                                            <Form.Item
                                                name="host_ssl_cert_id"
                                                label={t('Select a certificate')}
                                                rules={[{ required: true, message: t('Select a certificate') }]}
                                            >
                                                <Select
                                                    placeholder={
                                                        availableHostCertificates.length
                                                            ? t('Select a certificate')
                                                            : t('No certificates available on the current node')
                                                    }
                                                    loading={sslLoading}
                                                    disabled={!availableHostCertificates.length}
                                                    showSearch
                                                    optionFilterProp="children"
                                                    dropdownRender={(menu) => (
                                                        <>
                                                            {menu}
                                                            <Divider style={{ margin: '8px 0' }} />
                                                            <Space
                                                                style={{
                                                                    padding: '0 8px 4px',
                                                                    width: '100%',
                                                                    justifyContent: 'space-between',
                                                                }}
                                                            >
                                                                <Button type="link" href={userPath('/certificates')} target="_blank">
                                                                    {t('Manage certificates')}
                                                                </Button>
                                                                <Button type="link" onClick={loadSslCertificates} size="small">
                                                                    {t('Refresh')}
                                                                </Button>
                                                            </Space>
                                                        </>
                                                    )}
                                                >
                                                    {availableHostCertificates.map((cert) => (
                                                        <Option key={cert.id} value={cert.id}>
                                                            {cert.domain}
                                                            {cert.expires_at && t(' · Expires: {{expires_at}}', { expires_at: cert.expires_at })}
                                                            {cert.node_id && t(' · Node #{{node_id}}', { node_id: cert.node_id })}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                            {selectedNodeId &&
                                                !availableHostCertificates.length &&
                                                sslCertificates.host.length > 0 && (
                                                    <Alert
                                                        type="warning"
                                                        showIcon
                                                        message={t('No certificates available on the selected container node')}
                                                        description={t('Select a container on another node or upload the certificate again on this node.')}
                                                        style={{ marginBottom: 16 }}
                                                    />
                                                )}
                                        </>
                                    ) : (
                                        <Form.Item
                                            name="custom_ssl_config"
                                            label={t('Custom SSL configuration (JSON)')}
                                            rules={[{ required: true, message: t('Enter a custom SSL configuration') }]}
                                        >
                                            <TextArea
                                                rows={6}
                                                placeholder='{"certificate": "/path/to/cert.pem", "key": "/path/to/key.pem"}'
                                            />
                                        </Form.Item>
                                    )}
                                </>
                            ) : null
                        }
                    </Form.Item>

                    <Form.Item
                        name="is_enabled"
                        label={t('Enable site')}
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 站点详情模态框 */}
            <Modal
                title={t('Site details')}
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={null}
                width={700}
            >
                {currentDetail && (
                    <Descriptions bordered column={1}>
                        <Descriptions.Item label={t('Domains')}>{currentDetail.domain}</Descriptions.Item>
                        <Descriptions.Item label={t('Domain alias')}>{currentDetail.server_aliases || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('Container ID')}>{currentDetail.container_id}</Descriptions.Item>
                        <Descriptions.Item label={t('Proxy type')}>{getProxyTypeText(currentDetail.proxy_type)}</Descriptions.Item>
                        <Descriptions.Item label={t('Root directory')}>{currentDetail.root_path}</Descriptions.Item>
                        <Descriptions.Item label={t('Index file')}>{currentDetail.index_file || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('SSL status')}>
                            <Tag color={currentDetail.ssl_enabled ? 'success' : 'default'}>
                                {currentDetail.ssl_enabled ? t('Enabled') : t('Disabled')}
                            </Tag>
                        </Descriptions.Item>
                        {currentDetail.ssl_config && (
                            <Descriptions.Item label={t('SSL configuration')}>
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                    {JSON.stringify(currentDetail.ssl_config, null, 2)}
                                </pre>
                            </Descriptions.Item>
                        )}
                        {currentDetail.custom_config && (
                            <Descriptions.Item label={t('Custom configuration')}>
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                    {currentDetail.custom_config}
                                </pre>
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label={t('Status')}>
                            <Tag color={currentDetail.is_enabled ? 'success' : 'default'}>
                                {currentDetail.is_enabled ? t('Enabled') : t('Disabled')}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label={t('Created at')}>
                            {currentDetail.created_at ? new Date(currentDetail.created_at).toLocaleString('zh-CN') : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('Updated at')}>
                            {currentDetail.updated_at ? new Date(currentDetail.updated_at).toLocaleString('zh-CN') : '-'}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            <Modal
                title={targetSite ? t('Bind system certificate - {{domain}}', { domain: targetSite.domain }) : t('Bind system certificate')}
                open={systemCertModalVisible}
                confirmLoading={systemCertLoading}
                onOk={handleApplySystemCert}
                onCancel={() => {
                    setSystemCertModalVisible(false);
                    systemCertForm.resetFields();
                    setTargetSite(null);
                }}
                destroyOnClose
                width={520}
            >
                <Form form={systemCertForm} layout="vertical">
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={t('Bind from the system certificate store')}
                        description={t('System certificates are usually issued automatically (e.g., for HTTPS apps). Binding one will overwrite the current site\'s certificate configuration.')}
                    />
                    <Form.Item label={t('Site domain')}>
                        <Input value={targetSite?.domain} disabled />
                    </Form.Item>
                    <Form.Item
                        name="ssl_certificate_id"
                        label={t('System certificate')}
                        rules={[{ required: true, message: t('Select a system certificate') }]}
                    >
                        <Select
                            placeholder={hasSystemCertificates ? t('Select a system certificate') : t('No system certificates yet')}
                            disabled={!hasSystemCertificates}
                            loading={sslLoading}
                            showSearch
                            optionFilterProp="children"
                        >
                            {sslCertificates.system.map((cert) => (
                                <Option key={cert.id} value={cert.id}>
                                    {cert.domain}
                                    {cert.expires_at && t(' · Expires: {{expires_at}}', { expires_at: cert.expires_at })}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label={t('Force HTTPS')} name="force_https" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    <Form.Item label={t('Enable HSTS')} name="hsts" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default MySites;
